import {useCallback, useRef} from 'react'
import useApi from '../../../services/useApi'
import useFilters from '../../../hook/useFilters'
import {PROFIT_TREND_CHART_VIEW_OPTIONS} from '../utils'
import {generateColorsFromIndex} from '../../../utils/chartColors'
import {
  ProfitTrendReportType,
  IProfitTrendKpiData,
  IWeeklyBreakdownData,
  IProfitTrendChartData,
} from '../types'

const ENTITY_PARAM_MAP: Record<ProfitTrendReportType, string> = {
  customer: 'customer_id',
  product: 'product_id',
  brand: 'brand_id',
  purchaser_by_brand: 'purchaser_name',
  purchaser_by_supplier: 'purchaser_name',
  classification: 'classification',
  supplier: 'supplier_name',
  sales_rep: 'rep_email',
  customer_type: 'customer_type',
  customer_group: 'customer_group_id',
}

const useGetProfitTrend = (reportType: ProfitTrendReportType) => {
  const Api = useApi()
  const lastEntityValue = useRef<string | null>(null)

  const initialFilters = {
    report_type: reportType,
    [ENTITY_PARAM_MAP[reportType]]: '',
  }

  const {filters, setSingleFilter, setMultipleFilters} = useFilters(initialFilters)

  const {data: response, isFetching: isLoading} = Api.useGetQuery(
    '/analytics/profit/trend/charts',
    {
      queryId: `profit-trend-${reportType}-${filters?.period}`,
      filters: filters,
      isToast: false,
      isErrorPageRedirection: false,
    },
    {
      enabled: !!filters?.[ENTITY_PARAM_MAP[reportType]],
    }
  )

  const profitTrendChartData: IProfitTrendChartData = (() => {
    if (!response?.data?.weekly_profit_trend?.datasets?.length) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Profit %',
            data: [],
            borderColor: generateColorsFromIndex(0),
            backgroundColor: generateColorsFromIndex(0),
            fill: false,
          },
        ],
      }
    }

    // Find the "Profit %" dataset for the chart
    const profitPercentageDataset = response.data.weekly_profit_trend.datasets.find(
      (dataset: any) => dataset.label === 'Profit %'
    )

    if (!profitPercentageDataset) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Profit %',
            data: [],
            borderColor: generateColorsFromIndex(0),
            backgroundColor: generateColorsFromIndex(0),
            fill: false,
          },
        ],
      }
    }

    // Format week labels from "10_2025" format to "Week 10"
    const formattedLabels = response.data.weekly_profit_trend.labels.map((label: string) => {
      return label
    })

    return {
      labels: formattedLabels,
      datasets: [
        {
          label: 'Profit %',
          data: profitPercentageDataset.data || [],
          borderColor: generateColorsFromIndex(0),
          backgroundColor: generateColorsFromIndex(0),
          fill: false,
        },
      ],
    }
  })()

  // Calculate KPI data from the response
  const kpiData: IProfitTrendKpiData = (() => {
    if (!response?.data?.analytics) {
      return {
        currentMargin: 0,
        sixMonthsAgoMargin: 0,
        improvement: 0,
        growthRate: 0,
      }
    }

    const {current_margin, six_months_ago_margin, improvement, growth_rate} =
      response.data.analytics

    return {
      currentMargin: current_margin,
      sixMonthsAgoMargin: six_months_ago_margin,
      improvement: improvement,
      growthRate: growth_rate,
    }
  })()

  // Get weekly breakdown data
  const weeklyBreakdownData: IWeeklyBreakdownData[] = response?.data?.weekly_table_data || []

  const onProfitTrendDateRangeChange = (startDate: any, endDate: any) => {
    setMultipleFilters({
      start_date: startDate?.format('YYYY-MM-DD'),
      end_date: endDate?.format('YYYY-MM-DD'),
      period: PROFIT_TREND_CHART_VIEW_OPTIONS(
        startDate?.format('YYYY-MM-DD'),
        endDate?.format('YYYY-MM-DD')
      )[0].value,
    })
  }

  const onProfitTrendIdChange = useCallback(
    (entityValue: string) => {
      lastEntityValue.current = entityValue
      setMultipleFilters({
        [ENTITY_PARAM_MAP[reportType]]: entityValue,
      })
    },
    [reportType, setMultipleFilters]
  )

  const onProfitTrendViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSingleFilter('period', e.target.value)
  }

  return {
    profitTrendChartData,
    kpiData,
    weeklyBreakdownData,
    isLoading,
    onProfitTrendIdChange,
    onProfitTrendViewChange,
    onProfitTrendDateRangeChange,
    profitTrendFilters: filters,
  }
}

export default useGetProfitTrend
